import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../lib/gameSounds';
import { Watermark } from './Watermark';

/**
 * HypeFish overlay widget — octagonal cast-result popup.
 *
 * Modelled on Slots: invisible while idle, slides + scales in when a
 * viewer's cast becomes the current cast on the engine, plays the four
 * animation phases (cast → wait → bite → reveal) inside an octagonal
 * frame, slides back out when the engine clears `state.current`.
 *
 * Bootstraps from `/api/hypefish/snapshot?token=…` so a freshly-opened
 * browser source picks up an in-flight cast immediately, then accepts
 * live updates from the `game-state` socket event filtered for
 * `gameType === 'hypefish'`.
 */

type HypeFishRarity = 'uncommon' | 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
type HypeFishPhase = 'idle' | 'casting' | 'waiting' | 'biting' | 'revealing';
type HypeFishWeather = 'default' | 'night' | 'storm' | 'tropical';

interface HypeFishItem {
  id: string;
  name: string;
  rarity: HypeFishRarity;
  emoji: string;
  flavor: string;
  imageUrl?: string | null;
}

interface HypeFishCurrentCast {
  viewer: string;
  item: HypeFishItem;
  gems: number;
  phase: HypeFishPhase;
  phaseEndsAt: number;
  waitDurationMs: number;
}

interface HypeFishState {
  current?: HypeFishCurrentCast | null;
}

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface HypefishSnapshot {
  state: HypeFishState | null;
  config: unknown;
  isActive: boolean;
}

/* ───────────────────── palette + rarity ───────────────────── */

const RARITY: Record<
  HypeFishRarity,
  { label: string; frame: string; glow: string; gradient: string; sparkle: boolean }
> = {
  common: {
    label: 'Common',
    frame: '#94a3b8',
    glow: 'rgba(148,163,184,0.55)',
    gradient: 'linear-gradient(160deg, #475569 0%, #1e293b 100%)',
    sparkle: false,
  },
  uncommon: {
    label: 'Uncommon',
    frame: '#34d399',
    glow: 'rgba(52,211,153,0.65)',
    gradient: 'linear-gradient(160deg, #047857 0%, #064e3b 100%)',
    sparkle: false,
  },
  rare: {
    label: 'Rare',
    frame: '#60a5fa',
    glow: 'rgba(96,165,250,0.7)',
    gradient: 'linear-gradient(160deg, #1d4ed8 0%, #0c2e6e 100%)',
    sparkle: false,
  },
  epic: {
    label: 'Epic',
    frame: '#c084fc',
    glow: 'rgba(192,132,252,0.8)',
    gradient: 'linear-gradient(160deg, #7c3aed 0%, #3b1078 100%)',
    sparkle: false,
  },
  legendary: {
    label: 'Legendary',
    frame: '#fbbf24',
    glow: 'rgba(251,191,36,0.9)',
    gradient: 'linear-gradient(160deg, #f59e0b 0%, #b45309 60%, #7c2d12 100%)',
    sparkle: true,
  },
  mythic: {
    label: 'Mythic',
    frame: '#fb7185',
    glow: 'rgba(251,113,133,1)',
    gradient:
      'linear-gradient(135deg, #f97316 0%, #ec4899 25%, #a855f7 55%, #06b6d4 85%, #fde047 100%)',
    sparkle: true,
  },
};

const WEATHER: Record<HypeFishWeather, { water: string; surface: string }> = {
  default: {
    water:
      'linear-gradient(180deg, transparent 0%, transparent 40%, rgba(56,189,248,0.22) 55%, rgba(14,116,165,0.55) 100%)',
    surface: '#38bdf8',
  },
  night: {
    water:
      'linear-gradient(180deg, transparent 0%, transparent 40%, rgba(99,102,241,0.22) 55%, rgba(30,27,75,0.7) 100%)',
    surface: '#a5b4fc',
  },
  storm: {
    water:
      'linear-gradient(180deg, transparent 0%, transparent 40%, rgba(71,85,105,0.35) 55%, rgba(15,23,42,0.7) 100%)',
    surface: '#94a3b8',
  },
  tropical: {
    water:
      'linear-gradient(180deg, transparent 0%, transparent 40%, rgba(251,146,60,0.28) 55%, rgba(124,45,18,0.65) 100%)',
    surface: '#fdba74',
  },
};

function weatherFromConfig(config: unknown): HypeFishWeather {
  const c = config as { weatherMode?: unknown; custom?: { weatherMode?: unknown } } | null;
  const raw =
    typeof c?.weatherMode === 'string' ? c.weatherMode : (c?.custom?.weatherMode ?? 'default');
  return raw === 'night' || raw === 'storm' || raw === 'tropical' ? raw : 'default';
}

function alwaysShowFromConfig(config: unknown): boolean {
  const c = config as {
    alwaysShowWidget?: unknown;
    custom?: { alwaysShowWidget?: unknown };
  } | null;
  // Engine merges custom into top-level config at game start, so the
  // canonical read is `config.alwaysShowWidget`. Fall through to nested
  // custom as a defense against shape drift.
  if (typeof c?.alwaysShowWidget === 'boolean') return c.alwaysShowWidget;
  if (typeof c?.custom?.alwaysShowWidget === 'boolean') return c.custom.alwaysShowWidget;
  return false;
}

/* ───────────────────── animations ───────────────────── */

const ANIMATIONS = `
@keyframes hf-popIn { 0% { transform: translate(-50%, -50%) scale(0.6) rotate(-8deg); opacity: 0 } 65% { transform: translate(-50%, -50%) scale(1.06) rotate(2deg); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1) rotate(0); opacity: 1 } }
@keyframes hf-popOut { 0% { transform: translate(-50%, -50%) scale(1); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(0.7); opacity: 0 } }
@keyframes hf-octaGlow { 0%, 100% { filter: drop-shadow(0 0 24px var(--rarity-glow)) drop-shadow(0 0 8px var(--rarity-glow)) } 50% { filter: drop-shadow(0 0 48px var(--rarity-glow)) drop-shadow(0 0 14px var(--rarity-glow)) } }
@keyframes hf-octaPrism { from { background-position: 0% 50% } to { background-position: 200% 50% } }
@keyframes hf-bobberCast { 0% { transform: translate(-50%, -180%) rotate(-25deg); opacity: 0 } 70% { transform: translate(-50%, -30%) rotate(0deg); opacity: 1 } 100% { transform: translate(-50%, 0%) rotate(0deg); opacity: 1 } }
@keyframes hf-bobberBob { 0%, 100% { transform: translate(-50%, 0%) rotate(-3deg) } 50% { transform: translate(-50%, -10%) rotate(3deg) } }
@keyframes hf-bobberYank { 0% { transform: translate(-50%, 0%) } 30% { transform: translate(-50%, 80%); opacity: 1 } 100% { transform: translate(-50%, 110%); opacity: 0 } }
@keyframes hf-ripple { 0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0.7 } 100% { transform: translate(-50%, -50%) scale(3); opacity: 0 } }
@keyframes hf-splash { 0% { transform: translate(-50%, -50%) scale(0); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0 } }
@keyframes hf-bangPop { 0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0 } 70% { transform: translate(-50%, -50%) scale(1.15); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes hf-bubble { 0% { transform: translateY(0); opacity: 0 } 20% { opacity: 0.9 } 100% { transform: translateY(-140px); opacity: 0 } }
@keyframes hf-cardRise { 0% { transform: translate(-50%, 60%) scale(0.5); opacity: 0 } 60% { transform: translate(-50%, -52%) scale(1.05); opacity: 1 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes hf-sparkle { 0%, 100% { opacity: 0; transform: scale(0.6) } 40% { opacity: 1; transform: scale(1.2) } }
@keyframes hf-waterShake { 0%, 100% { transform: translate(0, 0) } 25% { transform: translate(-2px, 1px) } 50% { transform: translate(2px, -1px) } 75% { transform: translate(-1px, 2px) } }
`;

const ENTER_MS = 500;
const EXIT_MS = 360;
const OCTA_SIZE = 560;

// Regular octagon clip-path. The 29.3% / 70.7% values are sin/cos of 45°
// applied to a 1×1 box, which makes the angled cuts true 45° rather than
// stretched at non-square aspects.
const OCTAGON_CLIP =
  'polygon(29.3% 0%, 70.7% 0%, 100% 29.3%, 100% 70.7%, 70.7% 100%, 29.3% 100%, 0% 70.7%, 0% 29.3%)';

/* ───────────────────── component ───────────────────── */

export function HypefishWidget({
  gameStateEvent,
  streamerTier = 'free',
}: {
  gameStateEvent: GameStateEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const [snapshot, setSnapshot] = useState<HypefishSnapshot | null>(null);
  const bootstrappedRef = useRef(false);

  // Mount bootstrap via browser-source token — paint immediately if a
  // cast is already in flight when the source opens.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    bootstrappedRef.current = true;
    const ac = new AbortController();
    fetch(`/api/hypefish/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: HypefishSnapshot | null) => {
        if (json) setSnapshot(json);
      })
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  // Live socket updates — game-state carries every game's pubsub, filter
  // for hypefish only.
  useEffect(() => {
    if (!gameStateEvent || gameStateEvent.gameType !== 'hypefish') return;
    setSnapshot({
      state: gameStateEvent.state as HypeFishState | null,
      config: gameStateEvent.config,
      isActive: gameStateEvent.isActive,
    });
  }, [gameStateEvent]);

  const isActive = snapshot?.isActive ?? false;
  const current = isActive ? (snapshot?.state?.current ?? null) : null;
  const alwaysShow = alwaysShowFromConfig(snapshot?.config);
  return (
    <>
      <style>{ANIMATIONS}</style>
      <CastOctagon
        current={current}
        config={snapshot?.config}
        streamerTier={streamerTier}
        alwaysShow={alwaysShow}
        isActive={isActive}
      />
    </>
  );
}

/* ───────────────────── octagon popup ───────────────────── */

interface DisplayedCast {
  cast: HypeFishCurrentCast;
  /** Wall-clock ms when we first locked onto this cast. Drives a stable
   *  React key so the enter animation only fires on a NEW cast, not on
   *  re-renders for phase advances within the same cast. */
  shownAt: number;
}

function castKey(c: HypeFishCurrentCast | null): string | null {
  if (!c) return null;
  // The engine doesn't expose a queuedAt on `current` (only on queued
  // entries); use viewer+itemId as a structural identity. Same viewer +
  // same itemId back-to-back would mis-merge — extremely unlikely in
  // practice and harmless visually (the animation just re-keys when the
  // phase machine advances).
  return `${c.viewer}::${c.item.id}::${c.phaseEndsAt}`;
}

function CastOctagon({
  current,
  config,
  streamerTier,
  alwaysShow,
  isActive,
}: {
  current: HypeFishCurrentCast | null;
  config: unknown;
  streamerTier: 'free' | 'paid';
  alwaysShow: boolean;
  isActive: boolean;
}) {
  // Persistent mode: octagon stays mounted while the session is active,
  // interior swaps between an idle pond and the live cast playback. No
  // pop-in/pop-out animations — it's an always-on widget in this mode.
  if (alwaysShow && isActive) {
    return <PersistentOctagon current={current} config={config} streamerTier={streamerTier} />;
  }
  return <PopupOctagon current={current} config={config} streamerTier={streamerTier} />;
}

/** Always-on variant — frame visible whenever the session is active. */
function PersistentOctagon({
  current,
  config,
  streamerTier,
}: {
  current: HypeFishCurrentCast | null;
  config: unknown;
  streamerTier: 'free' | 'paid';
}) {
  const weather = weatherFromConfig(config);
  const isReveal = current?.phase === 'revealing';
  const rarity = isReveal && current ? RARITY[current.item.rarity] : null;
  const frameColor = rarity ? rarity.frame : 'rgba(255,255,255,0.45)';
  const frameGlow = rarity ? rarity.glow : 'rgba(255,255,255,0.25)';
  const isPrismatic = isReveal && current?.item.rarity === 'mythic';
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: OCTA_SIZE,
        height: OCTA_SIZE,
        transform: 'translate(-50%, -50%)',
        ['--rarity-glow' as string]: frameGlow,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isPrismatic && rarity ? rarity.gradient : frameColor,
          backgroundSize: isPrismatic ? '200% 100%' : undefined,
          animation: isReveal
            ? `hf-octaGlow 1.6s ease-in-out infinite${isPrismatic ? ', hf-octaPrism 3.5s linear infinite' : ''}`
            : undefined,
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          transition: 'background 300ms ease, filter 200ms ease',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 6,
          background: `radial-gradient(ellipse at 50% 0%, #0a2540 0%, #06141f 60%, #020c14 100%)`,
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          overflow: 'hidden',
        }}
      >
        {current ? <CastStage cast={current} weather={weather} /> : <IdleStage weather={weather} />}
        {streamerTier === 'free' && <CornerWatermark />}
      </div>
    </div>
  );
}

function PopupOctagon({
  current,
  config,
  streamerTier,
}: {
  current: HypeFishCurrentCast | null;
  config: unknown;
  streamerTier: 'free' | 'paid';
}) {
  const [displayed, setDisplayed] = useState<DisplayedCast | null>(null);
  const [phase, setPhase] = useState<'enter' | 'show' | 'exit'>('show');
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastViewerItemRef = useRef<string | null>(null);

  // React to current changes:
  // - new cast (different viewer/item than last) → enter
  // - same cast, phase advance → stay shown, just update state
  // - current === null and we have a displayed cast → exit
  useEffect(() => {
    if (current) {
      const key = `${current.viewer}::${current.item.id}`;
      if (key !== lastViewerItemRef.current) {
        lastViewerItemRef.current = key;
        setDisplayed({ cast: current, shownAt: Date.now() });
        setPhase('enter');
        // Lock to "show" once the enter animation completes so further
        // phase changes don't re-trigger it.
        if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
        const t = setTimeout(() => setPhase('show'), ENTER_MS);
        return () => clearTimeout(t);
      }
      // Same cast, refresh the inner data so phase advances flow through.
      setDisplayed((d) => (d ? { ...d, cast: current } : d));
      return;
    }
    // current became null → exit the displayed cast (if any), then clear.
    if (!displayed) return;
    lastViewerItemRef.current = null;
    setPhase('exit');
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(() => {
      setDisplayed(null);
      setPhase('show');
    }, EXIT_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(
    () => () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    },
    [],
  );

  if (!displayed) return null;
  const { cast } = displayed;
  const weather = weatherFromConfig(config);
  const r = RARITY[cast.item.rarity];
  // Pre-reveal: subdued neutral frame so the rarity isn't telegraphed
  // before the bite. The frame snaps to rarity color on reveal.
  const isReveal = cast.phase === 'revealing';
  const frameColor = isReveal ? r.frame : 'rgba(255,255,255,0.45)';
  const frameGlow = isReveal ? r.glow : 'rgba(255,255,255,0.25)';
  const isPrismatic = isReveal && cast.item.rarity === 'mythic';

  const wrapperAnim =
    phase === 'enter'
      ? `hf-popIn ${ENTER_MS}ms cubic-bezier(0.16,1,0.3,1) both`
      : phase === 'exit'
        ? `hf-popOut ${EXIT_MS}ms ease-in forwards`
        : undefined;

  return (
    <div
      key={castKey(cast)}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: OCTA_SIZE,
        height: OCTA_SIZE,
        transform: 'translate(-50%, -50%)',
        animation: wrapperAnim,
        ['--rarity-glow' as string]: frameGlow,
        pointerEvents: 'none',
      }}
    >
      {/* Octagon frame: outer color band + inner content pane, both
          clipped to the regular-octagon polygon. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isPrismatic ? r.gradient : frameColor,
          backgroundSize: isPrismatic ? '200% 100%' : undefined,
          animation: isReveal
            ? `hf-octaGlow 1.6s ease-in-out infinite${isPrismatic ? ', hf-octaPrism 3.5s linear infinite' : ''}`
            : undefined,
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          transition: 'background 300ms ease, filter 200ms ease',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 6,
          background: `radial-gradient(ellipse at 50% 0%, #0a2540 0%, #06141f 60%, #020c14 100%)`,
          clipPath: OCTAGON_CLIP,
          WebkitClipPath: OCTAGON_CLIP,
          overflow: 'hidden',
        }}
      >
        <CastStage cast={cast} weather={weather} />
        {streamerTier === 'free' && <CornerWatermark />}
      </div>
    </div>
  );
}

function CornerWatermark() {
  return (
    <div
      style={{
        position: 'absolute',
        right: 14,
        bottom: 14,
        pointerEvents: 'none',
        zIndex: 5,
        opacity: 0.7,
        transform: 'scale(0.75)',
        transformOrigin: 'bottom right',
      }}
    >
      <Watermark variant="inline" />
    </div>
  );
}

/** Empty-pond view rendered inside the persistent octagon between casts. */
function IdleStage({ weather }: { weather: HypeFishWeather }) {
  const ws = WEATHER[weather];
  const bubbles = Array.from({ length: 4 }, (_, i) => ({
    left: `${30 + i * 12 + Math.random() * 6}%`,
    delay: `${i * 600}ms`,
    size: 5 + Math.floor(Math.random() * 7),
  }));
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        animation: weather === 'storm' ? 'hf-waterShake 700ms ease-in-out infinite' : undefined,
      }}
    >
      {/* Title block */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: '"Orbitron",ui-sans-serif,system-ui,sans-serif',
          textShadow: '0 2px 6px rgba(0,0,0,0.7)',
          zIndex: 5,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: 4,
            textTransform: 'uppercase',
            color: '#bae6fd',
            fontWeight: 700,
          }}
        >
          🎣 HypeFish
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 18,
            fontWeight: 900,
            color: '#fff',
            letterSpacing: 1.5,
          }}
        >
          waiting on the line
        </div>
      </div>

      {/* Water layer */}
      <div style={{ position: 'absolute', inset: 0, background: ws.water }} />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '58%',
          height: 2,
          background: `linear-gradient(90deg, transparent, ${ws.surface}, transparent)`,
          opacity: 0.45,
        }}
      />

      {/* Idle bubbles */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
        {bubbles.map((b, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: b.left,
              bottom: '15%',
              width: b.size,
              height: b.size,
              borderRadius: '50%',
              background: 'rgba(186, 230, 253, 0.45)',
              border: '1px solid rgba(255,255,255,0.35)',
              animation: `hf-bubble 3600ms ease-out ${b.delay} infinite`,
            }}
          />
        ))}
      </div>

      {/* Call-to-action */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 60,
          transform: 'translateX(-50%)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 16px',
          borderRadius: 999,
          background: 'rgba(56,189,248,0.18)',
          border: '1px solid rgba(56,189,248,0.5)',
          fontFamily: '"Rajdhani",ui-sans-serif,system-ui,sans-serif',
          fontSize: 14,
          fontWeight: 700,
          color: '#fff',
          letterSpacing: 1,
          boxShadow: '0 0 16px rgba(56,189,248,0.35)',
          zIndex: 4,
        }}
      >
        type
        <span
          style={{
            fontFamily: '"JetBrains Mono",monospace',
            padding: '2px 8px',
            borderRadius: 6,
            background: 'rgba(0,0,0,0.4)',
            border: '1px solid rgba(255,255,255,0.25)',
            color: '#bae6fd',
            fontWeight: 800,
          }}
        >
          !cast
        </span>
        to fish
      </div>
    </div>
  );
}

/* ───────────────────── inner playback stage ───────────────────── */

function CastStage({ cast, weather }: { cast: HypeFishCurrentCast; weather: HypeFishWeather }) {
  const phase = cast.phase;
  const ws = WEATHER[weather];

  // Sound triggers on phase transitions.
  const prevPhaseRef = useRef<HypeFishPhase>('idle');
  useEffect(() => {
    if (phase !== prevPhaseRef.current) {
      if (phase === 'casting') void gameSounds.play('phase_change');
      else if (phase === 'biting') void gameSounds.play('correct');
      else if (phase === 'revealing') {
        if (cast.item.rarity === 'mythic' || cast.item.rarity === 'legendary') {
          void gameSounds.play('celebration');
        } else if (cast.item.rarity === 'epic') {
          void gameSounds.play('winner');
        } else {
          void gameSounds.play('reveal');
        }
      }
      prevPhaseRef.current = phase;
    }
  }, [phase, cast.item.rarity]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        animation: weather === 'storm' ? 'hf-waterShake 700ms ease-in-out infinite' : undefined,
      }}
    >
      {/* Top label: viewer who cast */}
      <ViewerTag viewer={cast.viewer} phase={phase} />

      {/* Water surface + animated layer */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: ws.water,
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '58%',
          height: 2,
          background: `linear-gradient(90deg, transparent, ${ws.surface}, transparent)`,
          opacity: 0.45,
        }}
      />

      {/* Bobber animations */}
      {phase !== 'revealing' && phase !== 'idle' && (
        <Bobber phase={phase} waitDurationMs={cast.waitDurationMs} />
      )}
      {(phase === 'waiting' || phase === 'biting') && <Bubbles />}
      {phase === 'casting' && <Ripple />}
      {phase === 'biting' && (
        <>
          <SplashBurst />
          <BiteIndicator />
        </>
      )}

      {/* Reveal */}
      {phase === 'revealing' && <RevealCard cast={cast} />}
      {phase === 'revealing' && cast.item.rarity === 'mythic' && <Sparkles />}
    </div>
  );
}

function ViewerTag({ viewer, phase }: { viewer: string; phase: HypeFishPhase }) {
  const label =
    phase === 'casting'
      ? 'casts…'
      : phase === 'waiting'
        ? 'waits for a bite'
        : phase === 'biting'
          ? 'FISH ON!'
          : phase === 'revealing'
            ? 'reels in'
            : '';
  return (
    <div
      style={{
        position: 'absolute',
        top: 28,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontFamily: '"Orbitron",ui-sans-serif,system-ui,sans-serif',
        textShadow: '0 2px 6px rgba(0,0,0,0.7)',
        zIndex: 5,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: '#bae6fd',
          fontWeight: 700,
        }}
      >
        🎣 HypeFish
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 20,
          fontWeight: 900,
          color: '#fff',
          letterSpacing: 1.5,
        }}
      >
        {viewer}
      </div>
      <div
        style={{
          fontSize: 12,
          color: 'rgba(255,255,255,0.75)',
          textTransform: 'uppercase',
          letterSpacing: 3,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function Bobber({ phase, waitDurationMs }: { phase: HypeFishPhase; waitDurationMs: number }) {
  const animation =
    phase === 'casting'
      ? 'hf-bobberCast 600ms cubic-bezier(0.34, 1.56, 0.64, 1) both'
      : phase === 'waiting'
        ? `hf-bobberBob ${Math.max(800, waitDurationMs / 3)}ms ease-in-out infinite`
        : phase === 'biting'
          ? 'hf-bobberYank 350ms ease-in forwards'
          : undefined;
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: '58%',
        width: 32,
        height: 48,
        transform: 'translate(-50%, 0%)',
        animation,
        zIndex: 3,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: -42,
          left: '50%',
          width: 2,
          height: 42,
          background: 'rgba(255,255,255,0.45)',
          transform: 'translateX(-50%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, #ef4444 0%, #b91c1c 50%, #fff 50%, #f3f4f6 100%)',
          borderRadius: '50% 50% 60% 60% / 60% 60% 40% 40%',
          boxShadow: '0 4px 10px rgba(0,0,0,0.5), inset 0 -3px 6px rgba(0,0,0,0.25)',
        }}
      />
    </div>
  );
}

function Ripple() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: '62%',
        width: 70,
        height: 14,
        borderRadius: '50%',
        border: '2px solid #38bdf8',
        animation: 'hf-ripple 800ms ease-out 200ms forwards',
      }}
    />
  );
}

function SplashBurst() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: '60%',
        width: 110,
        height: 110,
        borderRadius: '50%',
        border: '3px solid #38bdf8',
        animation: 'hf-splash 400ms ease-out forwards',
      }}
    />
  );
}

function BiteIndicator() {
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: '50%',
        top: '40%',
        fontSize: 52,
        fontWeight: 900,
        color: '#fbbf24',
        textShadow: '0 0 18px rgba(251,191,36,0.95), 0 4px 12px rgba(0,0,0,0.7)',
        WebkitTextStroke: '2px white',
        animation: 'hf-bangPop 300ms cubic-bezier(0.16,1,0.3,1) both',
        transform: 'translate(-50%, -50%)',
      }}
    >
      !
    </div>
  );
}

function Bubbles() {
  const bubbles = Array.from({ length: 5 }, (_, i) => ({
    left: `${35 + i * 7 + Math.random() * 6}%`,
    delay: `${i * 240}ms`,
    size: 5 + Math.floor(Math.random() * 8),
  }));
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2 }}>
      {bubbles.map((b, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: b.left,
            bottom: '20%',
            width: b.size,
            height: b.size,
            borderRadius: '50%',
            background: 'rgba(186, 230, 253, 0.55)',
            border: '1px solid rgba(255,255,255,0.4)',
            animation: `hf-bubble 2400ms ease-out ${b.delay} infinite`,
          }}
        />
      ))}
    </div>
  );
}

function Sparkles() {
  const pts = Array.from({ length: 12 }, (_, i) => ({
    left: `${10 + Math.random() * 80}%`,
    top: `${10 + Math.random() * 80}%`,
    delay: `${i * 140}ms`,
  }));
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 6 }}>
      {pts.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.left,
            top: p.top,
            width: 10,
            height: 10,
            background: 'radial-gradient(circle, #fff 0%, rgba(255,255,255,0) 70%)',
            animation: `hf-sparkle 1800ms ease-in-out ${p.delay} infinite`,
          }}
        />
      ))}
    </div>
  );
}

function RevealCard({ cast }: { cast: HypeFishCurrentCast }) {
  const r = RARITY[cast.item.rarity];
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '54%',
        width: 240,
        zIndex: 5,
        transform: 'translate(-50%, -50%)',
        animation: 'hf-cardRise 600ms cubic-bezier(0.16,1,0.3,1) both',
      }}
    >
      <div
        style={{
          padding: 3,
          borderRadius: 16,
          background: r.gradient,
          backgroundSize: cast.item.rarity === 'mythic' ? '200% 100%' : undefined,
          boxShadow: `0 0 28px ${r.glow}, 0 0 8px ${r.glow}`,
        }}
      >
        <div
          style={{
            borderRadius: 14,
            background: r.gradient,
            padding: '12px 14px 10px',
            color: '#fff',
            fontFamily: '"Rajdhani",ui-sans-serif,system-ui,sans-serif',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: 9,
              letterSpacing: 4,
              fontFamily: '"Orbitron",sans-serif',
              fontWeight: 800,
              opacity: 0.9,
              textTransform: 'uppercase',
            }}
          >
            {r.label}
          </div>
          <CardArt item={cast.item} />
          <div
            style={{
              marginTop: 4,
              fontSize: 18,
              fontWeight: 900,
              fontFamily: '"Orbitron",sans-serif',
              letterSpacing: 1,
              lineHeight: 1.05,
            }}
          >
            {cast.item.name}
          </div>
          <div
            style={{
              marginTop: 6,
              padding: '4px 12px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(255,255,255,0.3)',
              fontFamily: '"JetBrains Mono","Orbitron",monospace',
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            <span style={{ fontSize: 13 }}>💎</span>+{cast.gems}
          </div>
        </div>
      </div>
    </div>
  );
}

function CardArt({ item }: { item: HypeFishItem }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [item.imageUrl]);
  if (!item.imageUrl || failed) {
    return (
      <div
        aria-hidden
        style={{
          margin: '6px auto 0',
          width: 110,
          height: 110,
          display: 'grid',
          placeItems: 'center',
          fontSize: 64,
          lineHeight: 1,
          textShadow: '0 4px 12px rgba(0,0,0,0.5)',
        }}
      >
        {item.emoji}
      </div>
    );
  }
  return (
    <img
      src={item.imageUrl}
      alt=""
      width={120}
      height={120}
      onError={() => setFailed(true)}
      style={{
        margin: '6px auto 0',
        width: 120,
        height: 120,
        objectFit: 'cover',
        borderRadius: 10,
        boxShadow: '0 6px 18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
      }}
    />
  );
}
